/** Session state reducer — handles navigation, answer updates, grading results, and phase transitions. */
import type { StudySessionStep, StudySessionStepResponse } from '@formency/shared';
import type {
  SessionState,
  SessionAction,
  StepPhase,
  StepAnswer,
  StepResult,
  AnsweredStep,
} from './types';

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Returns the initial phase for a step based on its kind.
 * Both info and question steps start in 'idle'.
 */
function phaseForStep(_stepKind: string): StepPhase {
  return 'idle';
}

/**
 * Returns an empty answer for a given question type.
 * Info steps return { type: 'info' }.
 */
function emptyAnswer(questionType: string | null): StepAnswer {
  switch (questionType) {
    case 'mcq':
      return { type: 'mcq', selectedIds: [] };
    case 'true_false':
      return { type: 'true_false', selected: null };
    case 'text':
      return { type: 'text', value: '' };
    case 'cloze':
      return { type: 'cloze', blanks: {} };
    case 'cloze_diagram':
      return { type: 'cloze_diagram' as const, blanks: {} };
    case 'cloze_image':
      return { type: 'cloze_image', blanks: {} };
    case 'ranking':
      return { type: 'ranking', order: [] };
    case 'classify':
      return { type: 'classify', assignments: {} };
    case 'image_hotspot':
      return { type: 'image_hotspot', x: null, y: null };
    case 'numeric':
      return { type: 'numeric', value: null };
    default:
      return { type: 'info' };
  }
}

/**
 * Navigates to a step, restoring from the answered-steps cache if available.
 * Returns a partial state update for the target step.
 */
function navigateToStep(state: SessionState, targetIndex: number): Partial<SessionState> {
  const cached = state.answeredSteps.get(targetIndex);
  const step = state.steps[targetIndex];

  if (cached) {
    return {
      currentIndex: targetIndex,
      phase: 'graded',
      answer: cached.answer,
      result: cached.result,
      gradingPhase: null,
    };
  }

  return {
    currentIndex: targetIndex,
    phase: phaseForStep(step.stepKind),
    answer: emptyAnswer(step.questionType),
    result: null,
    gradingPhase: null,
  };
}

/**
 * If the current step is an info step that hasn't been recorded yet,
 * return a new answeredSteps Map with it marked as seen.
 */
function markInfoStepSeen(state: SessionState): SessionState['answeredSteps'] {
  const current = state.steps[state.currentIndex];
  if (current?.stepKind === 'info' && !state.answeredSteps.has(state.currentIndex)) {
    const next = new Map(state.answeredSteps);
    next.set(state.currentIndex, {
      answer: { type: 'info' },
      result: { isCorrect: true, score: 0, maxScore: 0, explanation: null, feedback: null },
      response: null,
    });
    return next;
  }
  return state.answeredSteps;
}

/** Find the frontier: first unanswered step index, or -1 when all done. */
function findFrontier(state: SessionState): number {
  for (let i = 0; i < state.steps.length; i++) {
    if (!state.answeredSteps.has(i)) return i;
  }
  return -1;
}

// ─── Reducer ─────────────────────────────────────────────────────────────────

export function sessionReducer(state: SessionState, action: SessionAction): SessionState {
  const result = sessionReducerInner(state, action);

  // Detect backward jumps — log enough context to diagnose the intermittent reset bug.
  if (
    result !== state &&
    result.currentIndex < state.currentIndex &&
    !['GO_BACK', 'GO_TO_STEP'].includes(action.type)
  ) {
    console.warn('[session-reducer] backward jump', {
      action: action.type,
      from: state.currentIndex,
      to: result.currentIndex,
      frontier: result.frontier,
      isComplete: result.isComplete,
      answeredCount: result.answeredSteps.size,
      totalSteps: result.steps.length,
    });
  }

  return result;
}

function sessionReducerInner(state: SessionState, action: SessionAction): SessionState {
  switch (action.type) {
    case 'UPDATE_ANSWER': {
      // Only allow answer updates in idle or answering phase
      if (state.phase !== 'idle' && state.phase !== 'answering') {
        return state;
      }
      return {
        ...state,
        phase: 'answering',
        answer: action.answer,
      };
    }

    case 'SUBMIT': {
      // Only allow submission from 'answering' phase
      if (state.phase !== 'answering') {
        return state;
      }
      return {
        ...state,
        phase: 'submitting',
      };
    }

    case 'SUBMIT_ERROR': {
      // Reset to answering phase so the user can retry after a failed submission
      if (state.phase !== 'submitting' && state.phase !== 'grading') {
        return state;
      }
      return {
        ...state,
        phase: 'answering',
        gradingPhase: null,
      };
    }

    case 'GRADING_PHASE': {
      // Accept grading events during submitting or grading phases
      if (state.phase !== 'submitting' && state.phase !== 'grading') {
        return state;
      }
      return {
        ...state,
        phase: 'grading',
        gradingPhase: action.event,
      };
    }

    case 'RECEIVE_RESULT': {
      // Accept results during submitting or grading phases
      if (state.phase !== 'submitting' && state.phase !== 'grading') {
        return state;
      }

      // Clone the Map for immutability
      const nextAnsweredSteps = new Map(state.answeredSteps);
      if (state.answer) {
        nextAnsweredSteps.set(state.currentIndex, {
          answer: state.answer,
          result: action.result,
          response: action.response,
        });
      }

      // Update readiness state if the response carried readiness fields.
      // Used by the in-session HUD and the break-modal gate (spec §11.1).
      // Both responseCount and episodeStartedAtMs are server-provided and
      // episode-scoped — so starting a new lesson mid-episode no longer
      // resets either of the modal-gate's accumulating thresholds.
      const prevReadiness = state.readiness;
      const nextReadiness = action.readiness
        ? {
            score: action.readiness.score,
            bars: action.readiness.bars,
            isColdStart: action.readiness.isColdStart,
            algorithmVersion: action.readiness.algorithmVersion,
            responseCount: action.readiness.episodeResponseCount,
            episodeStartedAtMs: action.readiness.episodeStartedAtMs,
            lastModalDismissedAtMs: action.readiness.lastModalDismissedAtMs,
            activeMinutesInEpisode: action.readiness.activeMinutesInEpisode,
            activeMinutesInSession: action.readiness.activeMinutesInSession,
            sessionStartedAtMs: action.readiness.sessionStartedAtMs,
          }
        : prevReadiness;

      // Algo v1.9.0 — append the new displayed score to the trend-arrow
      // history. Keep the last 5 entries; older values are dropped to bound
      // the buffer and keep the slope computation responsive to recent moves.
      const READINESS_HISTORY_SIZE = 5;
      const nextHistory = action.readiness
        ? [...state.readinessHistory, action.readiness.score].slice(-READINESS_HISTORY_SIZE)
        : state.readinessHistory;

      return {
        ...state,
        phase: 'graded',
        result: action.result,
        gradingPhase: null,
        answeredSteps: nextAnsweredSteps,
        frontier: findFrontier({ ...state, answeredSteps: nextAnsweredSteps }),
        isReviewing: false,
        readiness: nextReadiness,
        readinessHistory: nextHistory,
      };
    }

    case 'NEXT_STEP': {
      // Guard: no-op if already complete
      if (state.isComplete) return state;

      // Mark info steps as seen when navigating away
      const nextAnswered = markInfoStepSeen(state);
      const nextIndex = state.currentIndex + 1;

      // If we're past the last step, check whether all steps are actually answered.
      // Stale keyboard/swipe closures can fire rapid NEXT_STEP dispatches between
      // renders, skipping over unseen steps. If unanswered steps remain, redirect
      // to the frontier instead of prematurely completing.
      if (nextIndex >= state.steps.length) {
        const frontier = findFrontier({ ...state, answeredSteps: nextAnswered });
        if (frontier !== -1) {
          return {
            ...state,
            answeredSteps: nextAnswered,
            frontier,
            ...navigateToStep({ ...state, answeredSteps: nextAnswered }, frontier),
            isReviewing: false,
          };
        }
        return {
          ...state,
          answeredSteps: nextAnswered,
          frontier: -1,
          isComplete: true,
          isReviewing: false,
        };
      }

      const nextFrontier = findFrontier({ ...state, answeredSteps: nextAnswered });
      return {
        ...state,
        answeredSteps: nextAnswered,
        frontier: nextFrontier,
        ...navigateToStep({ ...state, answeredSteps: nextAnswered }, nextIndex),
        // Stop reviewing once we reach the frontier
        isReviewing: state.isReviewing && nextIndex < nextFrontier,
      };
    }

    case 'GO_BACK': {
      const prevIndex = state.currentIndex - 1;

      // Can't go before the first step
      if (prevIndex < 0) {
        return state;
      }

      return {
        ...state,
        ...navigateToStep(state, prevIndex),
        isReviewing: true,
      };
    }

    case 'GO_TO_STEP': {
      const { index } = action;

      // Bounds check
      if (index < 0 || index >= state.steps.length) {
        return state;
      }

      // Only allow navigation to already-answered steps, the current step,
      // or the next unanswered step (frontier) so progress bar groups can jump ahead.
      if (index !== state.currentIndex && !state.answeredSteps.has(index)) {
        if (index !== state.frontier) {
          return state;
        }
      }

      return {
        ...state,
        ...navigateToStep(state, index),
        isReviewing: state.answeredSteps.has(index) && index + 1 < state.frontier,
      };
    }

    case 'SUBMIT_INFO': {
      if (state.phase !== 'idle') return state;
      return { ...state, phase: 'submitting' };
    }

    case 'SESSION_AUTO_COMPLETED': {
      return {
        ...state,
        sessionAutoCompleted: true,
        completionData: action.completionData,
      };
    }

    case 'SYNC_SESSION': {
      const synced = buildInitialAnsweredSteps(action.steps);
      const tempState = { ...state, answeredSteps: synced, steps: action.steps };
      const frontier = findFrontier(tempState);

      if (frontier === -1) {
        return { ...state, answeredSteps: synced, steps: action.steps, isComplete: true };
      }
      return {
        ...state,
        answeredSteps: synced,
        steps: action.steps,
        frontier,
        // Explicitly clear isComplete — if a late SYNC_SESSION arrives with
        // unanswered steps, the session is NOT complete.
        isComplete: false,
        ...navigateToStep(tempState, frontier),
        isReviewing: false,
      };
    }

    case 'TOGGLE_SOUND': {
      const next = !state.soundEnabled;
      try {
        localStorage.setItem('formency-sound', next ? '1' : '0');
      } catch {
        /* ignore */
      }
      return {
        ...state,
        soundEnabled: next,
      };
    }

    default:
      return state;
  }
}

// ─── Server response → V2 type conversion ──────────────────────────────────

/**
 * Convert the raw API `responseJson` back into the V2 discriminated StepAnswer union.
 * Used when restoring previously answered steps on session resume.
 */
function responseJsonToStepAnswer(questionType: string | null, responseJson: unknown): StepAnswer {
  switch (questionType) {
    case 'mcq':
      return { type: 'mcq', selectedIds: Array.isArray(responseJson) ? responseJson : [] };
    case 'true_false': {
      // API stores as "true"/"false" string or ["true"]/["false"] array
      const raw = Array.isArray(responseJson) ? responseJson[0] : responseJson;
      return { type: 'true_false', selected: String(raw) === 'true' };
    }
    case 'text':
      return { type: 'text', value: typeof responseJson === 'string' ? responseJson : '' };
    case 'cloze':
      return {
        type: 'cloze',
        blanks:
          responseJson !== null && typeof responseJson === 'object' && !Array.isArray(responseJson)
            ? (responseJson as Record<string, string>)
            : {},
      };
    case 'cloze_diagram':
      return {
        type: 'cloze_diagram',
        blanks:
          responseJson !== null && typeof responseJson === 'object' && !Array.isArray(responseJson)
            ? (responseJson as Record<string, string>)
            : {},
      };
    case 'cloze_image':
      return {
        type: 'cloze_image',
        blanks:
          typeof responseJson === 'object' && responseJson !== null
            ? (responseJson as Record<string, string>)
            : {},
      };
    case 'ranking':
      return { type: 'ranking', order: Array.isArray(responseJson) ? responseJson : [] };
    case 'classify':
      return {
        type: 'classify',
        assignments:
          responseJson !== null && typeof responseJson === 'object' && !Array.isArray(responseJson)
            ? (responseJson as Record<string, string>)
            : {},
      };
    case 'image_hotspot': {
      const resp =
        typeof responseJson === 'object' && responseJson !== null
          ? (responseJson as { x?: number; y?: number })
          : {};
      return {
        type: 'image_hotspot',
        x: typeof resp.x === 'number' ? resp.x : null,
        y: typeof resp.y === 'number' ? resp.y : null,
      };
    }
    case 'numeric': {
      const resp =
        typeof responseJson === 'object' && responseJson !== null
          ? (responseJson as { value?: number; unit?: string })
          : {};
      return {
        type: 'numeric',
        value: typeof resp.value === 'number' ? resp.value : null,
        ...(typeof resp.unit === 'string' && { unit: resp.unit }),
      };
    }
    default:
      return { type: 'info' };
  }
}

/**
 * Build a StepResult from a server StudySessionStepResponse + the step's snapshot.
 */
function serverResponseToResult(
  response: StudySessionStepResponse,
  step: StudySessionStep
): StepResult {
  const feedbackJson = response.feedbackJson as { learnerFeedback?: string } | undefined;
  return {
    isCorrect: response.isCorrect ?? false,
    score: response.score ?? 0,
    maxScore: response.maxScore,
    explanation: step.stepSnapshotPublicJson?.explanation ?? null,
    feedback: feedbackJson?.learnerFeedback ?? null,
    ...(response.correctChoiceIds && { correctChoiceIds: response.correctChoiceIds }),
    ...(response.blankResults && { blankResults: response.blankResults }),
    ...(response.rankingResults && { rankingResults: response.rankingResults }),
    ...(response.classifyResults && { classifyResults: response.classifyResults }),
    ...(response.hotspotResult && { hotspotResult: response.hotspotResult }),
    ...(response.numericResult && { numericResult: response.numericResult }),
  };
}

/**
 * Pre-populate the answeredSteps cache from server responses for steps
 * that are already completed.
 *
 * - Info steps with `state === 'done'` are marked as seen.
 * - Question steps with `state === 'done'` and a server response are restored.
 * - Everything else is not in the map.
 */
export function buildInitialAnsweredSteps(steps: StudySessionStep[]): Map<number, AnsweredStep> {
  const map = new Map<number, AnsweredStep>();

  for (let i = 0; i < steps.length; i++) {
    const step = steps[i];

    // Info steps that are done
    if (step.stepKind === 'info' && step.state === 'done') {
      map.set(i, {
        answer: { type: 'info' },
        result: { isCorrect: true, score: 0, maxScore: 0, explanation: null, feedback: null },
        response: null,
      });
      continue;
    }

    // Question steps that are done with a server response
    const serverResponse = step.studySessionStepResponses?.[0];
    if (step.state === 'done' && serverResponse) {
      map.set(i, {
        answer: responseJsonToStepAnswer(step.questionType, serverResponse.responseJson),
        result: serverResponseToResult(serverResponse, step),
        response: serverResponse,
      });
    }
  }

  return map;
}

// ─── Initial state factory ───────────────────────────────────────────────────

export function createInitialState(
  sessionId: string,
  steps: SessionState['steps'],
  mode: SessionState['mode'],
  _startIndex?: number // kept for API compat but ignored
): SessionState {
  const answeredSteps = buildInitialAnsweredSteps(steps);

  // Derive start from step states: first unseen step
  let index = 0;
  for (let i = 0; i < steps.length; i++) {
    if (!answeredSteps.has(i)) {
      index = i;
      break;
    }
    if (i === steps.length - 1) {
      index = i; // All done, stay on last
    }
  }

  const allDone = steps.length > 0 && !steps.some((_, i) => !answeredSteps.has(i));

  // Restore cached answer/result if landing on an already-answered step
  const cached = answeredSteps.get(index);
  const step = steps[index];

  // Compute initial frontier
  const initialState: SessionState = {
    steps,
    currentIndex: index,
    phase: cached ? 'graded' : step ? phaseForStep(step.stepKind) : 'idle',
    answer: cached ? cached.answer : step ? emptyAnswer(step.questionType) : null,
    result: cached ? cached.result : null,
    gradingPhase: null,
    answeredSteps,
    sessionId,
    mode,
    isComplete: allDone,
    readiness: null,
    readinessHistory: [],
    soundEnabled: (() => {
      try {
        return localStorage.getItem('formency-sound') !== '0';
      } catch {
        return true;
      }
    })(),
    isReviewing: false,
    frontier: 0,
    sessionAutoCompleted: false,
    completionData: null,
  };
  initialState.frontier = findFrontier(initialState);
  return initialState;
}
